/* eslint-disable complexity */
import { hotelDefaultFilterForm, HotelFilterForm } from '@pages/travel/hotel/data-models';
import { HotelSearchPayload } from '@shared/models/hotel-search-payload.model';
import { HotelResult, HotelRoom } from '@shared/models/hotel-search-response.model';
import { MappedHotelSearchResult } from '@shared/models/search/mapped-hotel-search-result';
import { IProductGroupOrdering } from '@shared/shared/interfaces/site-settings.interfaces';
import { SELECTED_HOTEL_LOCAL_STORAGE_KEY } from 'app.config';
import * as moment from 'moment';
import * as fromSubBookingActions from '../../travel-booking/actions/travel-hotel-sub-booking.actions';
import * as fromActions from '../actions/hotel-search.actions';
import * as searchFormActions from '../actions/travel-search-form.actions';

export interface MealBasisDefinition {
  title: string;
  group_id: number;
}

export interface HotelSearchInitialState {
  hotelSearch: HotelSearchPayload;
  recommendedHotels: HotelResult[];
  hotelSearchResults: HotelSearchResult;
  visibleHotels: {
    min_room_price: number;
    max_room_price: number;
    results: Array<HotelResult>;
  };
  selectedHotel: HotelResult;
  selectedRoom: HotelRoom[];
  hotel_search_by_name: string;
  hotel_is_selected: boolean;
}

export interface HotelSearchResult {
  loading: boolean;
  loaded: boolean;
  min_room_price: number;
  max_room_price: number;
  results: Array<HotelResult>;
  search_reference_id: string;
  validationErrors: Array<any>;
  mappedSortedResults: Array<MappedHotelSearchResult>;
  filteredMappedSortedResults: Array<MappedHotelSearchResult>;
  defaultFilterForm: HotelFilterForm;
  hotelFilterForm: HotelFilterForm;
}

export const initialState: HotelSearchInitialState = {
  hotelSearch: {
    start_date_full: moment().add(1, 'd').hours(12).minutes(0).seconds(0).milliseconds(0).toString(),
    end_date_full: moment().add(2, 'd').hours(12).minutes(0).seconds(0).milliseconds(0).toString(),
    start_location_id: ['hotel_location'],
    type: 'location',
    room_requests: [{ num_adults: 2, num_infants: 0, children_ages: [] }],
    selected_start_location_name: '',
  },
  recommendedHotels: [],
  hotelSearchResults: {
    loading: false,
    loaded: false,
    min_room_price: null,
    max_room_price: null,
    results: [],
    search_reference_id: '',
    validationErrors: [],
    mappedSortedResults: [],
    filteredMappedSortedResults: [],
    defaultFilterForm: hotelDefaultFilterForm,
    hotelFilterForm: hotelDefaultFilterForm,
  },
  visibleHotels: {
    min_room_price: null,
    max_room_price: null,
    results: [],
  },
  selectedHotel: <any>{},
  selectedRoom: [],
  hotel_search_by_name: '',
  hotel_is_selected: false,
};

export function reducer(
  state = initialState,
  action:
    | fromActions.HotelSearchActions
    | fromSubBookingActions.TravelHotelSubBookingActions
    | searchFormActions.SetDefaultStartDate
): HotelSearchInitialState {
  switch (action.type) {
    case searchFormActions.SET_DEFAULT_START_DATE: {
      return {
        ...initialState,
        hotelSearch: {
          ...initialState.hotelSearch,
          start_date_full: action.payload.startDate,
          end_date_full: action.payload.endDate,
        },
      };
    }
    case fromActions.CLEAR_HOTEL_SEARCH:
      return {
        ...initialState,
        recommendedHotels: state.recommendedHotels,
        selectedHotel: action.data.clearSelectedHotel ? initialState.selectedHotel : state.selectedHotel,
      };

    case fromActions.CLEAR_SELECTED_HOTEL:
      return {
        ...state,
        hotelSearchResults: initialState.hotelSearchResults,
        selectedHotel: initialState.selectedHotel,
      };

    case fromActions.CHANGE_HOTEL_PREFERENCE: {
      const isIdEqual = (hotel: HotelResult) => hotel.product_id === action.id;
      const getUpdatedHotels = (hotels: HotelResult[]): HotelResult[] => {
        const index = hotels.findIndex(isIdEqual);
        let updatedHotels: HotelResult[];

        if (index !== -1) {
          updatedHotels = [...hotels];
          updatedHotels[index] = {
            ...updatedHotels[index],
            is_preferred: action.preferred,
          };
        }

        return updatedHotels;
      };
      const getUpdatedMappedHotels = (hotels: MappedHotelSearchResult[]) => {
        const index = hotels.findIndex((hotel) => hotel.productId === action.id);
        let updatedMappedHotels: MappedHotelSearchResult[];

        if (index !== -1) {
          updatedMappedHotels = [...hotels];
          updatedMappedHotels[index] = {
            ...updatedMappedHotels[index],
            preferred: action.preferred,
          };
        }

        return updatedMappedHotels;
      };
      const updatedRecommendedHotels = getUpdatedHotels(state.recommendedHotels);
      const updatedHotelSearchResultsResult = getUpdatedHotels(state.hotelSearchResults.results);
      const updatedVisibleHotelResult = getUpdatedHotels(state.hotelSearchResults.results);
      const updatedMappedHotels = getUpdatedMappedHotels(state.hotelSearchResults.mappedSortedResults);
      return {
        ...state,
        recommendedHotels: updatedRecommendedHotels || state.recommendedHotels,
        hotelSearchResults: updatedHotelSearchResultsResult
          ? {
              ...state.hotelSearchResults,
              results: updatedHotelSearchResultsResult,
              mappedSortedResults: updatedMappedHotels,
            }
          : state.hotelSearchResults,
        visibleHotels: updatedVisibleHotelResult
          ? {
              ...state.visibleHotels,
              results: updatedVisibleHotelResult,
            }
          : state.visibleHotels,
        selectedHotel: isIdEqual(state.selectedHotel)
          ? { ...state.selectedHotel, is_preferred: action.preferred }
          : state.selectedHotel,
      };
    }
    case fromActions.CHANGE_HOTEL_SEARCH_BY_NAME:
      return {
        ...state,
        hotel_search_by_name: action.hotelName,
        hotel_is_selected: action.hotelIsSelected,
      };
    case fromActions.RESTORE_HOTELS_SUCCESS:
      return {
        ...state,
        hotelSearch: action.payload.hotelSearch
          ? { ...state.hotelSearch, ...action.payload.hotelSearch }
          : state.hotelSearch,
        hotelSearchResults: action.payload.hotelSearchResults
          ? { ...state.hotelSearchResults, ...action.payload.hotelSearchResults }
          : state.hotelSearchResults,
        selectedHotel: action.payload.selectedHotel
          ? { ...state.selectedHotel, ...action.payload.selectedHotel }
          : state.selectedHotel,
        selectedRoom: action.payload.selectedRoom
          ? { ...state.selectedRoom, ...action.payload.selectedRoom }
          : state.selectedRoom,
      };

    case fromActions.CREATE_HOTEL_SEARCH:
      return {
        ...state,
        hotelSearch: {
          ...state.hotelSearch,
          ...action.payload,
        },
      };

    case fromActions.GET_HOTEL_SEARCH__RESULTS:
      return {
        ...state,
        hotelSearchResults: {
          ...state.hotelSearchResults,
          loading: true,
          loaded: false,
        },
      };

    case fromActions.GET_HOTEL_SEARCH__RESULTS_SUCCESS: {
      let hotels: HotelResult[] = JSON.parse(JSON.stringify([...action.payload.results]));
      hotels = hotels.filter((obj) => +obj.rating >= 3);

      const isPricePerNight = action.payload.travel_hotels_rooms_price_per_night;

      hotels = hotels.map((hotel: HotelResult) => {
        const fromPrice = {
          amount: hotel.rooms[0].total_price.amount * action.payload.room_requests + '',
          currency_code: hotel.rooms[0].total_price.currency_code,
        };
        return {
          ...hotel,
          from_price: isPricePerNight ? hotel.from_price : fromPrice,
        };
      });

      const hotelFromPrices = hotels.map((hotel: HotelResult) => +hotel.from_price.amount);
      const maxHotelPrice = Math.max(...hotelFromPrices);
      const minHotelPrice = Math.min(...hotelFromPrices);

      const results = hotels.sort((hotelX: any, hotelY: any) => {
        const isBenefitHotel = hotelY.is_benefits_hotel - hotelX.is_benefits_hotel;
        const sortByRating = hotelY.rating - hotelX.rating;
        const sortByPrice = hotelX.from_price.amount - hotelY.from_price.amount;

        if (isBenefitHotel) {
          return isBenefitHotel;
        }

        if (sortByRating) {
          return sortByRating;
        }

        if (sortByPrice) {
          return sortByPrice;
        }

        return 0;
      });

      return {
        ...state,
        hotelSearchResults: {
          ...state.hotelSearchResults,
          min_room_price: minHotelPrice,
          max_room_price: maxHotelPrice,
          search_reference_id: action.payload.search_reference_id,
          results: results,
          loading: false,
          loaded: true,
          mappedSortedResults: action.payload.mappedSortedHotels,
          defaultFilterForm: action.payload.defaultFilters,
        },
        visibleHotels: {
          ...state.visibleHotels,
          min_room_price: minHotelPrice,
          max_room_price: maxHotelPrice,
          results: results,
        },
      };
    }

    case fromActions.GET_HOTEL_SEARCH__RESULTS_FAILURE:
      return {
        ...state,
        hotelSearchResults: {
          ...initialState.hotelSearchResults,
          loading: false,
          loaded: true,
          validationErrors: action.payload,
        },
        visibleHotels: {
          min_room_price: null,
          max_room_price: null,
          results: [],
        },
      };

    case fromActions.CLEAR_FILTER_FORM:
      return {
        ...state,
        hotelSearchResults: {
          ...state.hotelSearchResults,
          hotelFilterForm: state.hotelSearchResults.defaultFilterForm,
        },
      };

    case fromActions.UPDATE_FILTER_FORM:
      return {
        ...state,
        hotelSearchResults: {
          ...state.hotelSearchResults,
          hotelFilterForm: action.payload,
        },
      };
    case fromActions.UPDATE_DEFAULT_HOTEL_FILTER_FORM:
      return {
        ...state,
        hotelSearchResults: {
          ...state.hotelSearchResults,
          defaultFilterForm: action.payload,
        },
      };
    case fromActions.GET_HOTEL_RECOMMENDED__RESULTS:
      return {
        ...state,
        hotelSearch: {
          ...state.hotelSearch,
        },
        hotelSearchResults: {
          ...state.hotelSearchResults,
          loading: true,
          loaded: false,
        },
      };

    case fromActions.GET_HOTEL_RECOMMENDED__RESULTS_SUCCESS:
      return {
        ...state,
        recommendedHotels: [...action.payload],
        hotelSearchResults: {
          ...state.hotelSearchResults,
          loading: false,
        },
      };

    case fromActions.GET_HOTEL_RECOMMENDED__RESULTS_FAILURE:
      return {
        ...state,
        recommendedHotels: [...action.payload],
      };

    case fromActions.FILTER_HOTEL_SEARCH_RESULTS: {
      const isBenefitsHotel =
        action.payload.data.args.ghc_only || action.payload.data.args.ghc_only === 'true' ? true : null;
      const isHotelWithInstalments =
        action.payload.data.args.instalments || action.payload.data.args.instalments === 'true' ? true : null;
      let amenities =
        action.payload.data.args.amenity !== null &&
        action.payload.data.args.amenity !== undefined &&
        action.payload.data.args.amenity.length !== 0
          ? action.payload.data.args.amenity
          : null;
      if (typeof amenities === 'string') {
        amenities = amenities.replace('[', '').replace(']', '').split(',');
      }
      let ratings = action?.payload?.data?.args?.property_class?.length
        ? action.payload.data.args.property_class
        : null;
      const maxPrice =
        action.payload.data.args.max_price !== null && action.payload.data.args.max_price !== undefined
          ? action.payload.data.args.max_price
          : state.hotelSearchResults.max_room_price;
      const minPrice =
        action.payload.data.args.min_price !== null && action.payload.data.args.min_price !== undefined
          ? action.payload.data.args.min_price
          : state.hotelSearchResults.min_room_price;

      const filteredResults = state.hotelSearchResults.results;

      const updatedResults = filteredResults
        .filter((hotel: any) => {
          let amenityFilter = true;
          if (!!amenities && amenities.length) {
            amenityFilter = amenities.every((a: string) => hotel.attribute_ids.includes(+a));
          }

          let ratingFilter = true;
          const halfRatings = [];
          if (ratings) {
            if (ratings.includes('3.0') && !ratings.includes('3.5')) {
              halfRatings.push('3.5');
            }

            if (ratings.includes('4.0') && !ratings.includes('4.5')) {
              halfRatings.push('4.5');
            }

            ratings = [...ratings, ...halfRatings];

            ratingFilter = ratings.includes(hotel.rating);
          }

          const isHotelWithInstalmentsFilter =
            isHotelWithInstalments !== null
              ? hotel.rooms.some((room: HotelRoom) => room.product_code && room.product_code === 'INS')
              : true;

          let isBenefitsHotelFilter = true;
          if (isBenefitsHotel) {
            isBenefitsHotelFilter = !!action.payload.data.args.productGroupOrdering.find(
              (groupOrdering: IProductGroupOrdering) => {
                const roomWithBenefit = hotel.rooms.find((room: HotelRoom) => {
                  // @mlicciardi
                  // TODO: remove this hardcoded value and move it to proper enum
                  if (!room.product_code || room.product_code === 'INS') return false;

                  return groupOrdering.product_code.includes(room.product_code);
                });

                return !!roomWithBenefit;
              }
            );
          }

          const roomPriceFilter = +hotel.from_price.amount >= minPrice && +hotel.from_price.amount <= maxPrice;

          return (
            amenityFilter && ratingFilter && isBenefitsHotelFilter && roomPriceFilter && isHotelWithInstalmentsFilter
          );
        })
        .sort((hotelX: any, hotelY: any) => {
          const isBenefitHotel = hotelY.is_benefits_hotel - hotelX.is_benefits_hotel;
          const sortByRating = hotelY.rating - hotelX.rating;
          const sortByPrice = hotelX.from_price.amount - hotelY.from_price.amount;

          if (isBenefitHotel) {
            return isBenefitHotel;
          }

          if (sortByRating) {
            return sortByRating;
          }

          if (sortByPrice) {
            return sortByPrice;
          }

          return 0;
        });
      return {
        ...state,
        visibleHotels: {
          ...state.visibleHotels,
          min_room_price: state.hotelSearchResults.min_room_price,
          max_room_price: state.hotelSearchResults.max_room_price,
          results: [...updatedResults],
        },
      };
    }
    case fromActions.SELECT_HOTEL_RESULT_SUCCESS: {
      let selectedHotel = state.hotelSearchResults.results.find((hotel) => {
        return hotel.product_id === action.payload.hotelId;
      });
      let is_recommended = false;
      if (!selectedHotel) {
        selectedHotel = state.recommendedHotels.find((hotel) => {
          return hotel.product_id === action.payload.hotelId;
        });
        is_recommended = !!selectedHotel;
      }
      const selectedHotelCopy = selectedHotel
        ? ({ ...selectedHotel, is_recommended } as HotelResult)
        : { ...selectedHotel };

      localStorage.setItem(SELECTED_HOTEL_LOCAL_STORAGE_KEY, JSON.stringify(selectedHotelCopy));
      return {
        ...state,
        visibleHotels: {
          ...state.visibleHotels,
          min_room_price: state.hotelSearchResults.min_room_price,
          max_room_price: state.hotelSearchResults.max_room_price,
          results: state.hotelSearchResults.results,
        },
        selectedHotel: selectedHotelCopy,
      };
    }
    case fromActions.SELECT_HOTEL_ROOM:
      return {
        ...state,
        selectedRoom: action.payload,
      };

    case fromSubBookingActions.CLEAR_HOTEL_SUB_BOOKING:
      return {
        ...state,
        selectedRoom: <any>{},
      };

    default:
      return state;
  }
}

export const getSearchData = (state: HotelSearchInitialState) => state.hotelSearch;
export const getSearchResultsData = (state: HotelSearchInitialState) => state.hotelSearchResults;
export const getVisibleHotels = (state: HotelSearchInitialState) => state.visibleHotels;
export const getHotelSearchResultsLoading = (state: HotelSearchInitialState) => state.hotelSearchResults.loading;
export const getHotelSearchResultsLoaded = (state: HotelSearchInitialState) => state.hotelSearchResults.loaded;
export const getHotel = (state: HotelSearchInitialState) => state.selectedHotel;
